import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ClipboardList, Pencil, Plus, Repeat, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { FREQUENCIES, catStyle } from "@/lib/constants";
import { fmtDeadline, fmtDuration } from "@/lib/time";
import JobDialog from "@/components/clients/JobDialog";
import OnboardDialog from "@/components/clients/OnboardDialog";
import { clientTypeLabel } from "@/lib/services";
import MiscTasks from "@/components/clients/MiscTasks";
import { StatusPill } from "@/components/jobs/StatusBox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const freqLabel = (f) => FREQUENCIES.find((x) => x.value === f)?.label || f;

function JobCard({ job, people, onEdit }) {
  const c = catStyle(job.category);
  return (
    <button onClick={() => onEdit && onEdit(job)} disabled={!onEdit} data-testid={`job-card-${job.id}`}
      className={`text-left rounded-2xl border border-[#103080]/10 bg-white p-5 hover:-translate-y-[1px] hover:shadow-md transition-[transform,box-shadow] duration-200 ${job.active ? "" : "opacity-50"}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: c.bg, color: c.text }}>{job.category}</span>
        <span className="text-xs text-[#0F172A]/50 inline-flex items-center gap-1"><Repeat size={11} />{freqLabel(job.frequency)}</span>
      </div>
      <p className="font-serif-brand font-semibold text-lg text-[#103080] mt-3">{job.title}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-[#0F172A]/60">
        <span>Next due <b className="text-[#103080]" data-testid={`job-next-due-${job.id}`}>{fmtDeadline(job.next_due)}</b></span>
        <span>{fmtDuration(job.duration_minutes)} · starts {job.lead_days}d before</span>
        <span className="col-span-2">Assigned to {people[job.assignee_id] || "Unknown"}{!job.active && " · Paused"}</span>
      </div>
    </button>
  );
}

function CardLine({ c }) {
  const done = c.tasks.filter((t) => t.status === "done").length;
  const all = c.tasks.length;
  const complete = all > 0 && done === all;
  return (
    <li className="flex items-center gap-3 py-3 border-b border-[#103080]/10 last:border-0 text-sm" data-testid={`client-card-${c.id}`}>
      <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${complete ? "bg-[#103080] border-[#103080] text-white" : "border-[#103080]/30"}`}>{complete && <Check size={12} />}</span>
      <span className={`flex-1 truncate text-[#103080] ${complete ? "line-through opacity-50" : "font-medium"}`}>{c.title}{c.period_label && <span className="text-[#0F172A]/45 font-normal"> · {c.period_label}</span>}</span>
      <span className="text-xs text-[#0F172A]/50 shrink-0">Due {fmtDeadline(c.deadline)}</span>
      <StatusPill status={c.status} testId={`client-card-status-${c.id}`} />
      <span className="text-xs shrink-0 w-24 text-right text-[#0090D0] font-semibold">{done}/{all} tasks</span>
    </li>
  );
}

export default function ClientDetail({ client, onEdit, onChanged, onDeleted }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState({});
  const [jobDialog, setJobDialog] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [onboarding, setOnboarding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [j, t] = await Promise.all([api.get(`/clients/${client.id}/jobs`), api.get(`/clients/${client.id}/cards`)]);
      setJobs(j.data);
      setTasks(t.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [client.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/directory").then((r) => {
      setDirectory(r.data);
      setPeople(Object.fromEntries(r.data.map((p) => [p.id, p.name])));
    }).catch(() => {});
  }, []);

  const remove = async () => {
    if (!window.confirm(`Delete ${client.name}? Their recurring jobs and open tasks will be removed.`)) return;
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success("Client deleted");
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const afterJob = () => { load(); onChanged(); };

  return (
    <div className="space-y-6" data-testid="client-detail">
      <section className="rounded-2xl border border-[#103080]/10 bg-[#F5F2EB] ambient p-6 sm:p-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-serif-brand font-semibold text-3xl text-[#103080] truncate" data-testid="client-detail-name">{client.name}</h2>
          {!isAdmin ? null : clientTypeLabel(client) ? (
            <span className="mt-2 inline-block rounded-full bg-white border border-[#103080]/15 px-3 py-1 text-xs font-semibold text-[#103080]" data-testid="client-detail-type">{clientTypeLabel(client)}</span>
          ) : (
            <button onClick={onEdit} className="mt-2 text-xs text-[#0090D0] font-semibold hover:underline" data-testid="client-set-type">Set client type</button>
          )}
          {client.notes && <p className="mt-2 text-sm text-[#0F172A]/60 max-w-xl">{client.notes}</p>}
        </div>
        {isAdmin && <div className="flex gap-2 shrink-0">
          <button onClick={onEdit} data-testid="client-edit-button" aria-label="Edit client"
            className="w-10 h-10 rounded-full border border-[#103080]/15 bg-white text-[#103080] flex items-center justify-center hover:bg-[#103080] hover:text-white transition-colors duration-200"><Pencil size={15} /></button>
          <button onClick={remove} data-testid="client-delete-button" aria-label="Delete client"
            className="w-10 h-10 rounded-full border border-red-200 bg-white text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors duration-200"><Trash2 size={15} /></button>
        </div>}
      </section>

      <Tabs defaultValue="services" className="space-y-6">
      <TabsList className="rounded-full bg-[#F5F2EB] p-1 h-auto">
        <TabsTrigger value="services" className="rounded-full px-5 py-2 data-[state=active]:text-[#103080]" data-testid="client-tab-services">Services & job cards</TabsTrigger>
        <TabsTrigger value="misc" className="rounded-full px-5 py-2 data-[state=active]:text-[#103080]" data-testid="client-tab-misc">Misc tasks</TabsTrigger>
      </TabsList>
      <TabsContent value="misc"><MiscTasks client={client} directory={directory} isAdmin={isAdmin} /></TabsContent>
      <TabsContent value="services" className="space-y-6">
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-brand font-semibold text-xl text-[#103080]">Services & recurring jobs</h3>
          {isAdmin && <div className="flex flex-wrap gap-2 justify-end">
            <button onClick={() => setOnboarding(true)} data-testid="onboard-services-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white font-medium px-5 h-10 text-sm hover:bg-[#0090D0] transition-colors duration-200">
              <ClipboardList size={15} /> Onboard services
            </button>
            <button onClick={() => setJobDialog({})} data-testid="add-job-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#103080]/20 bg-white text-[#103080] font-medium px-5 h-10 text-sm hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200">
              <Plus size={15} /> Custom job
            </button>
          </div>}
        </div>
        {jobs.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-[#103080]/20 p-8 text-center text-sm text-[#0F172A]/50" data-testid="jobs-empty">
            No services yet. Click Onboard services to set up VAT, accounts, tax returns or payroll.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="job-list">
            {jobs.map((j) => <JobCard key={j.id} job={j} people={people} onEdit={isAdmin ? setJobDialog : null} />)}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient p-6">
        <h3 className="font-serif-brand font-semibold text-xl text-[#103080] mb-2">{isAdmin ? "Job cards for this client" : "Your job cards for this client"}</h3>
        {tasks.length === 0 ? (
          <p className="text-sm text-[#0F172A]/50 py-4" data-testid="client-tasks-empty">Job cards appear here as each deadline comes up.</p>
        ) : (
          <ul data-testid="client-card-list">{tasks.map((c) => <CardLine key={c.id} c={c} />)}</ul>
        )}
      </section>
      </TabsContent>
      </Tabs>

      <JobDialog job={jobDialog} clientId={client.id} directory={directory} templates={templates} roles={roles} defaultAssignee={user.id}
        onClose={() => setJobDialog(null)} onSaved={afterJob} />
      <OnboardDialog open={onboarding} client={client} directory={directory} templates={templates} roles={roles} defaultAssignee={user.id}
        onClose={() => setOnboarding(false)} onSaved={afterJob} />
    </div>
  );
}
